import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Paper,
  Stack,
  Divider,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { formatDateField } from "../../../../../../base/FormatTime";

// A styled container for each section of the modal
const Section = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
    <Typography
      variant="subtitle1"
      component="h3"
      sx={{ fontWeight: "bold", mb: 2, color: "text.primary" }}
    >
      {title}
    </Typography>
    {children}
  </Paper>
);

// A styled component for displaying a single key-value pair
const DetailItem = ({ label, value, isLast = false }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      py: 1.5,
      borderBottom: !isLast ? 1 : 0,
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", flex: 1 }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, textAlign: "right" }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: "medium", color: "text.primary" }}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  </Box>
);

// Helper function to decode the boolean choice value into text
const getChoiceLabel = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "Not answered";
};

const FallsRiskAssessmentDetailsModal = ({ open, handleClose, assessment }) => {
  if (!assessment) {
    return null;
  }

  const questions = [
    {
      key: "has_fallen_in_last_12_months",
      label: "Has service user fallen in last 12 months?",
      details: [{ key: "fallen_how_many_times", label: "How many times?" }],
    },
    {
      key: "is_dizzy_or_lightheaded",
      label: "Dizzy or lightheaded when getting up?",
      details: [{ key: "dizzy_lightheaded_comment", label: "Comment" }],
    },
    {
      key: "taking_more_than_4_medications",
      label: "Taking more than 4 medications?",
      details: [
        { key: "medications_list", label: "Medications" },
        { key: "medication_comment", label: "Comment" },
      ],
    },
    {
      key: "has_neurological_disorder",
      label: "Neurological Disorder?",
      details: [
        { key: "neurological_disorders", label: "Disorders" },
        { key: "neurological_disorder_comment", label: "Comment" },
      ],
    },
    {
      key: "is_agitated_or_confused",
      label: "Agitated or confused?",
      details: [{ key: "agitated_or_confused_comment", label: "Comment" }],
    },
    {
      key: "alcohol_intake_increases_risk",
      label: "Alcohol intake increasing falls risk?",
      details: [{ key: "alcohol_intake_comment", label: "Comment" }],
    },
    {
      key: "is_eyesight_impaired",
      label: "Eyesight impaired?",
      details: [{ key: "eyesight_impaired_comment", label: "Comment" }],
    },
    {
      key: "has_incontinence_or_urgency",
      label: "Incontinence or urgency to go to toilet?",
      details: [{ key: "incontinence_or_urgency_comment", label: "Comment" }],
    },
    {
      key: "has_trips_or_hazards_at_home",
      label: "Trips/hazards around home?",
      details: [{ key: "trips_or_hazards_comment", label: "Comment" }],
    },
    {
      key: "has_difficulty_getting_up",
      label: "Difficulty getting up from bed/chair?",
      details: [{ key: "difficulty_getting_up_comment", label: "Comment" }],
    },
    {
      key: "has_previous_fractures_or_osteoporosis",
      label: "Previous fractures/osteoporosis?",
      details: [{ key: "previous_fractures_comment", label: "Comment" }],
    },
    {
      key: "has_worries_about_falling",
      label:
        "Does the service user have any worries or concerns about risk of falling?",
      details: [{ key: "worries_about_falling_comment", label: "Comment" }],
    },
    {
      key: "any_other_comments",
      label: "Any other comments?",
      details: [{ key: "other_comments_text", label: "Comment" }],
    },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Falls Risk Assessment Details
          </Typography>
          <IconButton onClick={handleClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: "grey.50", p: 3 }}>
        <Stack spacing={2}>
          <Section title="General Information">
            <DetailItem label="Service User" value={assessment.client_name} />
            <DetailItem
              label="Date of Assessment"
              value={formatDateField(assessment.created_at)}
            />
            <DetailItem label="Created By" value={assessment.created_by_name} />
            <DetailItem label="Care Plan" value={assessment.care_plan_name} />
            <DetailItem
              label="Total Risk Score"
              isLast
              value={assessment.score}
            />
          </Section>

          <Section title="Assessment Responses">
            {questions.map((q, index) => (
              <Box
                key={q.key}
                sx={{
                  py: 1,
                  borderBottom: index !== questions.length - 1 ? 1 : 0,
                  borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {q.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      color: assessment[q.key] ? "error.main" : "success.main",
                    }}
                  >
                    {getChoiceLabel(assessment[q.key])}
                  </Typography>
                </Box>
                {assessment[q.key] && q.details && (
                  <Box
                    sx={{
                      pl: 2,
                      mt: 1.5,
                      borderLeft: 2,
                      borderColor: "grey.300",
                    }}
                  >
                    {q.details.map((detail) => {
                      const detailValue = Array.isArray(assessment[detail.key])
                        ? assessment[detail.key].join(", ")
                        : assessment[detail.key];

                      return detailValue ? (
                        <Box
                          key={detail.key}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", mr: 2 }}
                          >
                            {detail.label}:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ textAlign: "right" }}
                          >
                            {detailValue}
                          </Typography>
                        </Box>
                      ) : null;
                    })}
                  </Box>
                )}
              </Box>
            ))}
          </Section>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FallsRiskAssessmentDetailsModal;
